import { getClient } from 'api'
import { AxiosError, AxiosRequestConfig } from 'axios'
import { BestOffer } from 'components/BestOfferBox/BestOffer.interface'
import { useEffect, useState } from 'react'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { logInfo } from 'utils/log'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { useAsyncEffect } from './useAsyncEffect'

export interface BestOfferResponse {
  result: {
    bestOffer: BestOffer
  } | null
  errors: unknown[]
  status: number
}

export enum BestOfferRequestState {
  Loading,
  Completed,
  Updating
}

export const useBestOffer = () => {
  const { rootStore } = useRootStore()
  const {
    traversal: {
      data: {
        system: { advertisementId },
        common: { amount, term, purpose }
      }
    },
    urlParams: { productId }
  } = rootStore

  const [bestOffer, setBestOffer] = useState<BestOffer | null>(null)
  const [bestOfferRequestState, setBestOfferRequestState] =
    useState<BestOfferRequestState>(BestOfferRequestState.Loading)

  useAsyncEffect(async () => {
    if (!advertisementId || !amount || !term || !purpose || amount < 500) {
      return
    }

    const requestConfig: AxiosRequestConfig = {
      params: { amount, term, purpose, productId }
    }

    setBestOfferRequestState(
      bestOffer ? BestOfferRequestState.Updating : BestOfferRequestState.Loading
    )

    try {
      const response = await getClient('paas').get<BestOfferResponse>(
        `/bestoffer/${advertisementId}`,
        requestConfig
      )

      const { result } = response.data

      setBestOffer(result ? result.bestOffer : null)
    } catch (error) {
      setBestOffer(null)
      logAndTrackError(error as AxiosError)
    } finally {
      setBestOfferRequestState(BestOfferRequestState.Completed)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advertisementId, amount, term, purpose, productId])

  useEffect(() => {
    // We need to update the productId in the traversal, so we see in the traversal what product the user saw last.
    if (bestOffer) {
      rootStore.traversal.setProductId(+bestOffer.product.id)

      logInfo(`Set productId to "${bestOffer.product.id}".`)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bestOffer])

  return { bestOffer, bestOfferRequestState }
}
